import React, { useState } from 'react';
import { BeakerIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { login } from '../services/authService';
import { Button, FormField, Input } from './ui';

interface LoginProps {
  onLogin: (username: string, password: string) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username || !password) {
      setError('Por favor ingrese nombre de usuario y contraseña');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await login({ username, password });
      onLogin(username, password);
    } catch (err: any) {
      let errorMessage = 'Error al iniciar sesión';

      if (err.response) {
        if (err.response.status === 401) {
          errorMessage = 'Credenciales inválidas';
        } else if (err.response.status === 403) {
          errorMessage = 'Usuario inactivo o bloqueado';
        } else if (err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">
      <div className="bg-white p-8 sm:p-10 rounded-xl shadow-xl w-full max-w-md border border-slate-200">
        <div className="flex justify-center mb-5">
          <div className="flex items-center justify-center h-14 w-14 rounded-xl bg-brand-700 text-white">
            <BeakerIcon className="h-7 w-7" />
          </div>
        </div>
        <h1 className="text-xl font-semibold text-center text-slate-900">NEA Panel</h1>
        <p className="text-sm text-center text-slate-500 mt-1 mb-8">Sistema de gestión de análisis de laboratorio</p>

        {error && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-md mb-6">
            <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <FormField label="Nombre de usuario" htmlFor="username">
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Ingresá tu usuario"
              disabled={isLoading}
              autoFocus
            />
          </FormField>

          <FormField label="Contraseña" htmlFor="password">
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Ingresá tu contraseña"
              disabled={isLoading}
            />
          </FormField>

          <Button type="submit" size="lg" loading={isLoading} className="w-full">
            {isLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
          </Button>
        </form>

        <p className="mt-10 text-center text-xs text-slate-400">
          © {new Date().getFullYear()} NEA Ambiental. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
};

export default Login;
